import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import { Calendar, ChevronDown, FilePlus2, Sparkles, FileText, Layers } from 'lucide-react';
import { client } from '@/lib/api';
import { formatDateInZone, formatTimeInZone } from '@/lib/timezone';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import EventDocView from '@/components/event-doc/EventDocView';
import CombinedDocView from '@/components/event-doc/CombinedDocView';
import {
  NewBlankEventDialog,
  NewFromTemplateDialog,
} from '@/components/EventDialogs';

type EventListItem = InferResponseType<typeof client.api.events.$get, 200>[number];

interface EventsTabProps {
  projectId: string;
  venueName: string | null | undefined;
}

type ViewMode = 'per-event' | 'combined';

export default function EventsTab({ projectId, venueName }: EventsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showBlank, setShowBlank] = useState(false);
  const [showFromTemplate, setShowFromTemplate] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('per-event');

  const { data: events, isLoading } = useQuery({
    queryKey: ['events', projectId],
    queryFn: async () => {
      const res = await client.api.events.$get({ query: { projectId } });
      if (!res.ok) throw new Error('Failed to load events');
      return res.json();
    },
    enabled: !!projectId,
  });

  const selectedEventId = searchParams.get('event');

  useEffect(() => {
    if (viewMode === 'per-event' && !selectedEventId && events && events.length > 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('event', events[0]!.id);
          return next;
        },
        { replace: true },
      );
    }
  }, [selectedEventId, events, setSearchParams, viewMode]);

  const setSelectedEvent = (id: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('event', id);
      else next.delete('event');
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/30">
            <button
              type="button"
              onClick={() => setViewMode('per-event')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'per-event'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="size-3.5" />
              Per event
            </button>
            <button
              type="button"
              onClick={() => setViewMode('combined')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'combined'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Layers className="size-3.5" />
              Combined doc
            </button>
          </div>
          {events && (
            <p className="text-xs text-muted-foreground ml-2">
              {events.length} event{events.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm">
              <Calendar className="size-4" />
              New event
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setShowFromTemplate(true)}>
              <Sparkles className="size-4" />
              From template
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowBlank(true)}>
              <FilePlus2 className="size-4" />
              Blank event
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading events…</p>
      ) : !events || events.length === 0 ? (
        <Card className="rounded-3xl shadow-ambient">
          <CardContent className="py-16 text-center space-y-3">
            <Calendar className="size-8 text-muted-foreground mx-auto" />
            <div>
              <p className="font-medium">No events yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Wedding-day timelines, welcome dinners, brunches — anything time-bound.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowFromTemplate(true)}>
                <Sparkles className="size-4" />
                Start from template
              </Button>
              <Button size="sm" onClick={() => setShowBlank(true)}>
                <FilePlus2 className="size-4" />
                Blank event
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'combined' ? (
        <CombinedDocView projectId={projectId} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 no-print" data-no-print>
            {events.map((evt) => (
              <EventCard
                key={evt.id}
                event={evt}
                isSelected={evt.id === selectedEventId}
                onSelect={() => setSelectedEvent(evt.id)}
              />
            ))}
          </div>
          {selectedEventId && (
            <EventDocView key={selectedEventId} eventId={selectedEventId} projectId={projectId} />
          )}
        </>
      )}

      <NewBlankEventDialog
        projectId={projectId}
        venueName={venueName}
        open={showBlank}
        onOpenChange={setShowBlank}
      />
      <NewFromTemplateDialog
        projectId={projectId}
        venueName={venueName}
        open={showFromTemplate}
        onOpenChange={setShowFromTemplate}
      />
    </div>
  );
}

function EventCard({
  event,
  isSelected,
  onSelect,
}: {
  event: EventListItem;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left rounded-2xl border bg-card p-4 transition-all ${
        isSelected
          ? 'border-foreground shadow-sm ring-1 ring-foreground/10'
          : 'border-border hover:border-foreground/40 hover:shadow-sm'
      }`}
    >
      <p className="label-caps text-[10px] text-muted-foreground">
        {formatDateInZone(event.startAt, event.timezone, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })}
      </p>
      <h3 className="display-tight text-lg mt-1">{event.name}</h3>
      <p className="text-xs text-muted-foreground mt-1 tabular-nums">
        {formatTimeInZone(event.startAt, event.timezone)} –{' '}
        {formatTimeInZone(event.endAt, event.timezone)}
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        {event.stepCount} step{event.stepCount === 1 ? '' : 's'}
        {event.location && (
          <>
            {' · '}
            <span className="truncate">{event.location}</span>
          </>
        )}
      </p>
    </button>
  );
}
